import { Box, IconButton, Typography, Radio, InputAdornment, Tooltip, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import React, { useEffect, useState } from 'react';
import { ApplicantProfileData } from '../../../../pages/rec/applicant/ApplicantProfile';
import { formatDate } from '../../../../utils/date';
import { useNotification } from '../../../../hooks/useNotification';
import TextField from '../../../common/TextField';
import EditIcon from '../../../common/icons/EditIcon';
import DatePickerField from '../../../common/DatePickerField';
import dayjs from 'dayjs';
import Button from '../../../common/Button';
import Notification from '../../../common/Notification';
import api from '../../../../services/httpClient';
import { applicantService } from '../../../../services/applicant.service';
import type {
  ApplicantStageView,
  ApplicantStagesResponse,
} from '../../../../services/types/applicant.types';
import { APPLICANT_ENDPOINTS } from '../../../../services/endpoints/rec/applicant.endpoints';
import { VACANCY_ENDPOINTS } from '../../../../services/endpoints/rec/vacancy.endpoints';
import { useParams } from 'react-router-dom';
import Loading from '../../../common/Loading';
import ConfirmationModal from '../../../common/ConfirmationModal';
import { usePermissions } from '../../../../context/PermissionContext';
import { API_HEADERS } from '../../../../constants/apiHeaders';
import { PAGE_IDS, PERMISSION_IDS } from '../../../../constants/permissions';
import ResendIcon from '../../../../assets/icons/Resend.svg';
import InfoIcon from '../../../../assets/icons/Info.svg';
import { useAuth } from '../../../../hooks/useAuth';
import {
  normalizeApplicantBasicInfo,
} from '../../../../utils/messageUtils';

interface ApplicantQuestion {
  id: number;
  question: string;
  answerType: 'text' | 'number' | 'decimal' | 'boolean' | 'date' | 'currency';
  answerText: string | null;
  answerNumber: number | null;
  answerDecimal: number | null;
  answerCurrency: number | null;
  answerBoolean: boolean | null;
  answerDate: string | null;
  sortOrder: number;
  updatedAt?: string | null;
  /** GENERAL stage only: the applicant column the answer is written to. */
  targetColumn?: string;
  /** EXISTING_DATA = copied from the applicant record, never asked. */
  answerSource?: string;
  /** Display label from the question-answers API, when the backend sends one. */
  answerStatus?: string | null;
  status?: string | null;
  isResent?: boolean;
  resentCount?: number;
  answeredById?: number | null;
  answeredBy?: string | null;
  answeredAt?: string | null;
  sentAt?: string | null;
}

/** Questions (or any per-stage value) keyed by vacancyStageId. */
type StageQuestionLists = Record<number, ApplicantQuestion[]>;

interface QuestionAnswerMeta {
  backgroundColor: string;
  color: string;
  /** Timestamp shown inside the colored pill. */
  displayText: string;
  /** Sent / Resent / who answered — shown on hover only. */
  tooltip: string;
}

interface ResendQuestionResponse {
  message: string;
  questionAnswerId: number;
  rephrasedText: string;
  updatedAt?: string;
}

interface SaveApplicantAnswerResponse {
  message: string;
  applicantId: number;
  answerId: number;
  answeredAt?: string;
  sentAt?: string;
  answeredBy?: string;
  answeredById?: number | null;
}



const QUESTION_LABEL_PENDING_BG = 'rgb(254, 243, 199)';
const QUESTION_LABEL_ANSWERED_APPLICANT_BG = 'rgb(210, 246, 188)';
const QUESTION_LABEL_ANSWERED_RECRUITER_BG = 'rgb(167, 227, 145)';
const QUESTION_LABEL_ANSWERED_RECRUITER_TEXT = '#15803D';

/** Formats ISO date/time using values from the string (no timezone conversion). */
export const formatQuestionAnswerTimestampFromIso = (
  value: string | null | undefined,
): string => {
  if (!value) return '';
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);
  if (!match) {
    return formatQuestionAnswerTimestamp(value);
  }
  const [, year, month, day, hour24, minute] = match;
  if (!hour24 || minute === undefined) {
    return `${month}.${day}.${year}`;
  }
  const hour = parseInt(hour24, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${month}.${day}.${year} • ${hour12}:${minute} ${ampm}`;
};

const isQuestionAnswered = (question: ApplicantQuestion): boolean => {
  switch (question.answerType) {
    case 'text':
      return Boolean(question.answerText?.trim());
    case 'number':
      return question.answerNumber != null;
    case 'decimal':
      return question.answerDecimal != null;
    case 'currency':
      return question.answerCurrency != null;
    case 'boolean':
      return question.answerBoolean != null;
    case 'date':
      return Boolean(question.answerDate?.trim());
    default:
      return false;
  }
};

const resolveQuestionAnswerMeta = (question: ApplicantQuestion): QuestionAnswerMeta | null => {
  if (isQuestionAnswered(question)) {
    const answeredByApplicant =
      question.answeredById === 0 ||
      (question.answeredById == null && question.answeredBy === 'Applicant');

    const timestamp =
      formatQuestionAnswerTimestampFromIso(question.answeredAt) ||
      formatQuestionAnswerTimestampFromIso(question.sentAt);

    if (!timestamp) {
      return null;
    }

    if (answeredByApplicant) {
      return {
        backgroundColor: QUESTION_LABEL_ANSWERED_APPLICANT_BG,
        color: '#23B848',
        displayText: timestamp,
        tooltip: 'Answered by Applicant',
      };
    }

    const recruiterName = question.answeredBy?.trim() || 'Recruiter';
    return {
      backgroundColor: QUESTION_LABEL_ANSWERED_RECRUITER_BG,
      color: QUESTION_LABEL_ANSWERED_RECRUITER_TEXT,
      displayText: timestamp,
      tooltip: `Answered by ${recruiterName}`,
    };
  }

  if (!question.sentAt) {
    return null;
  }

  if (question.isResent) {
    return {
      backgroundColor: QUESTION_LABEL_PENDING_BG,
      color: '#D97706',
      displayText: formatQuestionAnswerTimestampFromIso(question.sentAt),
      tooltip: 'Resent',
    };
  }

  return {
    backgroundColor: QUESTION_LABEL_PENDING_BG,
    color: '#D97706',
    displayText: formatQuestionAnswerTimestampFromIso(question.sentAt),
    tooltip: 'Sent',
  };
};

const formatQuestionAnswerTimestamp = (value: string | null | undefined): string => {
  if (!value) return '';
  const parsed = dayjs(value);
  if (!parsed.isValid() || parsed.year() < 100) return '';
  const hasTime = /[T\s]\d{1,2}:\d{2}/.test(value);
  return hasTime ? parsed.format('MM.DD.YYYY • h:mm A') : parsed.format('MM.DD.YYYY');
};

const resendButtonAriaLabel = 'Re-send question';

const getResendButtonTooltip = (
  question: ApplicantQuestion,
  canResendForStage: boolean,
): string => {
  if (question.isResent) {
    return 'Waiting for a new answer';
  }
  if (!isQuestionAnswered(question)) {
    return 'Available after the question is answered';
  }
  if (!canResendForStage) {
    return 'Not available for this stage';
  }
  const count = question.resentCount ?? 0;
  if (count > 0) {
    return count === 1 ? 'Re-sent once before' : `Re-sent ${count} times before`;
  }
  return 'Re-send on WhatsApp';
};

const buildResendConfirmMessage = (question: ApplicantQuestion | null): string => {
  const rephrasedNotice =
    'The candidate will receive a rephrased version of this question on WhatsApp. Their previous answer will be cleared until they respond.';

  if (!question) {
    return rephrasedNotice;
  }

  const count = question.resentCount ?? 0;
  if (count <= 0) {
    return rephrasedNotice;
  }

  const alreadySent =
    count === 1
      ? 'You have already re-sent this question once.'
      : `You have already re-sent this question ${count} times.`;

  return `${alreadySent}<br /><br />${rephrasedNotice}<br /><br />Only continue if the candidate still has not answered clearly.`;
};

const findQuestionById = (
  lists: StageQuestionLists,
  questionAnswerId: number,
): ApplicantQuestion | null => {
  for (const questions of Object.values(lists)) {
    const match = questions.find((q) => q.id === questionAnswerId);
    if (match) {
      return match;
    }
  }
  return null;
};

interface AdditionalInfoProps {
  setSummary?: (summary: ApplicantProfileData['basicInfo']) => void;
}

// Helper function to format number with thousand separators
const formatNumberWithCommas = (value: string | number | null | undefined): string => {
  if (!value && value !== 0) return '';
  const numStr = value.toString().replace(/,/g, '');
  if (numStr === '' || isNaN(Number(numStr))) return '';
  const num = parseFloat(numStr);
  if (isNaN(num)) return '';
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

// Helper function to remove commas and get numeric value
const removeCommas = (value: string): string => {
  return value.replace(/,/g, '');
};

const toNonNegativeNumber = (value: string): number => {
  const parsedValue = parseFloat(value);
  return Number.isNaN(parsedValue) ? 0 : Math.max(0, parsedValue);
};

const toNullableNonNegativeNumber = (value: string): number | null =>
  value === '' ? null : toNonNegativeNumber(value);

const salaryInputText = (value: number | null | undefined): string =>
  value == null ? '' : String(value);

const toNonNegativeText = (value: string): string => {
  if (value === '') {
    return '';
  }
  return toNonNegativeNumber(value).toString();
};

const toNonNegativeNullableNumber = (value: number | null): number | null => {
  return value === null ? null : Math.max(0, value);
};

const sanitizeQuestionAnswer = (question: ApplicantQuestion): ApplicantQuestion => ({
  ...question,
  answerNumber: toNonNegativeNullableNumber(question.answerNumber),
  answerDecimal: toNonNegativeNullableNumber(question.answerDecimal),
  answerCurrency: toNonNegativeNullableNumber(question.answerCurrency),
});

const sortQuestionsByOrder = (questions: ApplicantQuestion[]): ApplicantQuestion[] =>
  [...questions].sort((a, b) => a.sortOrder - b.sortOrder);

const applyResendToQuestion = (
  question: ApplicantQuestion,
  resend: ResendQuestionResponse
): ApplicantQuestion => ({
  ...question,
  // Preserve the original question text on resend. The rephrased version
  // (resend.rephrasedText) is only sent to the candidate on WhatsApp and must
  // not replace the text recruiters see in the UI.
  answerText: null,
  answerNumber: null,
  answerDecimal: null,
  answerCurrency: null,
  answerBoolean: null,
  answerDate: null,
  answeredBy: null,
  answeredById: null,
  answeredAt: null,
  isResent: true,
  resentCount: (question.resentCount ?? 0) + 1,
  updatedAt: resend.updatedAt ?? new Date().toISOString(),
});

const applySaveAnswerToQuestion = (
  saved: ApplicantQuestion,
  response: SaveApplicantAnswerResponse,
  recruiterDisplayName: string,
): ApplicantQuestion => {
  const answeredAt =
    response.answeredAt?.trim() || dayjs().format('YYYY-MM-DDTHH:mm:ss');
  const sentAt = response.sentAt?.trim() || answeredAt;
  const answeredBy = response.answeredBy?.trim() || recruiterDisplayName;
  const answeredByIdFromApi = response.answeredById;
  const answeredById =
    typeof answeredByIdFromApi === 'number' && answeredByIdFromApi > 0
      ? answeredByIdFromApi
      : null;

  return {
    ...saved,
    answeredAt,
    sentAt,
    answeredBy,
    answeredById,
    updatedAt: answeredAt,
    // A fresh answer clears the "resent / waiting for a new answer" state so the
    // resend button becomes available again now that the question is answered.
    isResent: false,
  };
};

const patchQuestionInGroupedLists = (
  lists: StageQuestionLists,
  questionAnswerId: number,
  patch: (question: ApplicantQuestion) => ApplicantQuestion
): StageQuestionLists => {
  const patched: StageQuestionLists = {};
  Object.entries(lists).forEach(([stageId, questions]) => {
    patched[Number(stageId)] = questions.map((q) => (q.id === questionAnswerId ? patch(q) : q));
  });
  return patched;
};

const isVietnam = (countryName: string): boolean =>
  /viet\s*nam/i.test(countryName);

const resolveSalaryCurrency = async (vacancyCode: string, applicantCountry?: string): Promise<string> => {
  if (!vacancyCode) {
    // If no vacancy, check applicant. If no applicant, default to USD.
    return applicantCountry ? (isVietnam(applicantCountry) ? 'VND' : 'USD') : 'USD';
  }

  try {
    const vacancyListResponse = await api.get<{ vacancies: { id: number; vacancyName: string; vacancyCode: string }[] }>(VACANCY_ENDPOINTS.list);
    const vacancies = vacancyListResponse.data.vacancies || [];
    const matchingVacancy = vacancies.find(v => v.vacancyCode === vacancyCode);

    if (!matchingVacancy) {
      return applicantCountry ? (isVietnam(applicantCountry) ? 'VND' : 'USD') : 'USD';
    }

    const vacancyResponse = await api.get<{
      locations?: Array<{
        country?: { id: number; name: string };
      }>;
    }>(
      VACANCY_ENDPOINTS.byId(matchingVacancy.id),
      {
        headers: { ...API_HEADERS.VACANCY_FORM_DEFAULT },
      }
    );

    const locations = vacancyResponse.data.locations || [];
    const currencies = new Set<string>();

    for (const location of locations) {
      if (location.country?.name) {
        currencies.add(isVietnam(location.country.name) ? 'VND' : 'USD');
      }
    }

    if (currencies.size === 1) {
      return Array.from(currencies)[0];
    }

    // Mixed vacancy (both Vietnam and non-Vietnam locations): use the applicant's assigned country
    if (applicantCountry) {
      return isVietnam(applicantCountry) ? 'VND' : 'USD';
    }
    // Ultimate fallback if both vacancy and applicant country are missing
    return 'USD';
  } catch (error) {
    console.error('Error fetching vacancy/currency data:', error);
    return applicantCountry ? (isVietnam(applicantCountry) ? 'VND' : 'USD') : 'USD';
  }
};

const AdditionalInfo: React.FC<AdditionalInfoProps> = ({ setSummary }) => {
  const { id } = useParams();
  const [refresh, setRefresh] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditFirst, setIsEditFirst] = useState(false);
  const [isEditSecond, setIsEditSecond] = useState(false);
  const { pageId, setPageId, hasPermission } = usePermissions();
  const [values, setValues] = useState<ApplicantProfileData['basicInfo'] | null>(null);
  const [initialValues, setInitialValues] = useState<ApplicantProfileData['basicInfo'] | null>(null);
  const { notification, hideNotification, showNotification } = useNotification();
  // Vacancy stages the applicant has reached (GET /applicants/{id}/stages), in pipeline order.
  const [stagesInfo, setStagesInfo] = useState<ApplicantStagesResponse | null>(null);
  const stages: ApplicantStageView[] = stagesInfo?.stages ?? [];
  const [applicantQuestions, setApplicantQuestions] = useState<StageQuestionLists>({});
  const [canResendForStage, setCanResendForStage] = useState<Record<number, boolean>>({});

  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [salaryCurrency, setSalaryCurrency] = useState<string>('');
  const [editingQuestionIds, setEditingQuestionIds] = useState<Set<number>>(new Set());
  const [draftAnswers, setDraftAnswers] = useState<StageQuestionLists>({});
  const [savingQuestionId, setSavingQuestionId] = useState<number | null>(null);
  const [currentSalaryInput, setCurrentSalaryInput] = useState<string>('');
  const [expectedSalaryInput, setExpectedSalaryInput] = useState<string>('');
  const [resendingQuestionId, setResendingQuestionId] = useState<number | null>(null);
  const [resendConfirmQuestionId, setResendConfirmQuestionId] = useState<number | null>(null);
  const [triggeringStageId, setTriggeringStageId] = useState<number | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!id || pageId !== PAGE_IDS.APPLICANT_ADDITIONAL_INFO) {
      setPageId(PAGE_IDS.APPLICANT_ADDITIONAL_INFO);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const fetchApplicantData = async () => {
      const response = await api.get<{ data: ApplicantProfileData['basicInfo'] }>(APPLICANT_ENDPOINTS.basic(id),
        {
          headers: { ...API_HEADERS.APPLICANT_ADDITIONAL_INFO_BASIC_GET },
        }
      );
      const basicData = normalizeApplicantBasicInfo(
        response.data.data as Record<string, unknown>,
      ) as ApplicantProfileData['basicInfo'];
      const currency = await resolveSalaryCurrency(basicData.vacancyCode || '', basicData.vacancyCountry);
      setSalaryCurrency(currency);
      setValues(basicData);
      setInitialValues(basicData);
      if (setSummary) {
        setSummary(basicData);
      }
      setIsLoading(false);
    };

    fetchApplicantData();
  }, [id, refresh, setSummary, pageId, setPageId]);

  useEffect(() => {
    if (isEditFirst && values) {
      setCurrentSalaryInput(salaryInputText(values.currentSalary));
      setExpectedSalaryInput(salaryInputText(values.expectedSalary));
    }
    // Intentionally only re-run when edit mode toggles on; including `values` would
    // overwrite in-progress user input on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditFirst]);

  /**
   * Loads the stages the applicant has reached with their questions. `quiet` keeps the loading
   * state off and keeps drafts of stages whose questions did not change (after a save or a trigger).
   */
  const fetchApplicantStages = async (quiet = false) => {
    if (!id) return;

    if (!quiet) setIsLoadingQuestions(true);
    try {
      const response = await applicantService.getApplicantStages(Number(id));
      const info = response.data;
      setStagesInfo(info);

      const loaded: StageQuestionLists = {};
      const resend: Record<number, boolean> = {};
      (info.stages || []).forEach((stage) => {
        loaded[stage.vacancyStageId] = sortQuestionsByOrder(
          (stage.questions || []).map((q) => sanitizeQuestionAnswer(q as ApplicantQuestion)),
        );
        resend[stage.vacancyStageId] = Boolean(stage.canResend);
      });
      setCanResendForStage(resend);

      setApplicantQuestions(loaded);
      setDraftAnswers((prev) => {
        const next: StageQuestionLists = {};
        Object.entries(loaded).forEach(([stageId, questions]) => {
          const existing = prev[Number(stageId)];
          // A quiet refresh must not drop answers the recruiter is typing.
          next[Number(stageId)] =
            quiet && existing && existing.length === questions.length
              ? existing
              : JSON.parse(JSON.stringify(questions));
        });
        return next;
      });
    } catch (error: any) {
      console.error('Error fetching applicant stages:', error);
    } finally {
      if (!quiet) setIsLoadingQuestions(false);
    }
  };

  useEffect(() => {
    fetchApplicantStages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (isLoading || !values) {
    return <Loading />;
  }

  const salaryFields = [
    {
      key: 'currentSalary',
      label: salaryCurrency ? `Current Salary (${salaryCurrency})` : 'Current Salary',
      value: values?.currentSalary != null ? `${salaryCurrency ? salaryCurrency + ' ' : ''}${values.currentSalary.toLocaleString()}` : 'N/A',
      width: 'calc(25% - 12px)'
    },
    {
      key: 'expectedSalary',
      label: salaryCurrency ? `Expected Salary (${salaryCurrency})` : 'Expected Salary',
      value: values?.expectedSalary != null ? `${salaryCurrency ? salaryCurrency + ' ' : ''}${values.expectedSalary.toLocaleString()}` : 'N/A',
      width: 'calc(25% - 12px)'
    },
  ];

  const joiningFields = [
    { label: 'Notice Period (in days)', value: values?.noticePeriod ? `${values.noticePeriod} Days` : 'N/A', width: 'calc(25% - 12px)' },
    { label: 'Joining Date', value: values?.joiningDate ? formatDate(values.joiningDate) : 'N/A', width: 'calc(25% - 12px)' },
    { label: 'Joining Note', value: values?.joiningNote ? values.joiningNote : 'N/A', width: 'calc(25% - 12px)' },
  ];

  const handleSaveChanges = async () => {
    const applicantId = id;
    if (applicantId === undefined) {
      return;
    }
    if (values.joiningDate && !dayjs(values.joiningDate).isValid()) {
      showNotification('Please enter a valid joining date', 'error');
      return;
    }
    try {
      const payload = {
        currentSalary: values.currentSalary,
        expectedSalary: values.expectedSalary,
        noticePeriod: values.noticePeriod,
        joiningDate: values.joiningDate,
        joiningNote: values.joiningNote,
      }
      setIsSaving(true);
      await api.put(APPLICANT_ENDPOINTS.updateAdditional(applicantId), payload,
        {
          headers: { ...API_HEADERS.APPLICANT_ADDITIONAL_INFO_UPDATE },
        }
      );

      showNotification('Changes saved successfully', 'success');
      setRefresh(refresh + 1);
    } catch (error) {
      showNotification('Error saving changes', 'error');
      console.error('Error saving changes:', error);
    } finally {
      setIsEditFirst(false);
      setIsEditSecond(false);
      setIsSaving(false);
    }
  };

  const handleAnswerChange = (questionId: number, field: string, value: any) => {
    setDraftAnswers((prev) => {
      const updated: StageQuestionLists = {};
      Object.entries(prev).forEach(([stageId, questions]) => {
        updated[Number(stageId)] = questions.map((q) =>
          q.id === questionId ? { ...q, [field]: value } as ApplicantQuestion : q
        );
      });
      return updated;
    });
  };

  const handleSaveAnswer = async (questionId: number, stageId: number) => {
    const applicantId = id;
    if (applicantId === undefined) {
      return;
    }
    const q = (draftAnswers[stageId] ?? []).find((q) => q.id === questionId);
    if (!q) return;
    if (q.answerType === 'date' && q.answerDate && !dayjs(q.answerDate).isValid()) {
      showNotification('Please enter a valid date', 'error');
      return;
    }
    try {
      setSavingQuestionId(questionId);

      const response = await api.put<SaveApplicantAnswerResponse>(
        APPLICANT_ENDPOINTS.applicantAnswers(applicantId),
        {
          id: q.id,
          answerType: q.answerType,
          answerText: q.answerType === 'text' ? q.answerText : null,
          answerNumber: q.answerType === 'number' ? toNonNegativeNullableNumber(q.answerNumber) : null,
          answerDecimal: q.answerType === 'decimal' ? toNonNegativeNullableNumber(q.answerDecimal) : null,
          answerCurrency: q.answerType === 'currency' ? toNonNegativeNullableNumber(q.answerCurrency) : null,
          answerBoolean: q.answerType === 'boolean' ? q.answerBoolean : null,
          answerDate: q.answerType === 'date' ? (q.answerDate || null) : null,
        },
        {
          headers: { ...API_HEADERS.APPLICANT_ADDITIONAL_INFO_SAVE_ANSWER },
        },
      );

      const recruiterDisplayName = user?.name?.trim() || user?.email || 'Recruiter';
      const savedQuestion = applySaveAnswerToQuestion(
        JSON.parse(JSON.stringify(q)) as ApplicantQuestion,
        response.data,
        recruiterDisplayName,
      );

      setApplicantQuestions((prev) => ({
        ...prev,
        [stageId]: (prev[stageId] ?? []).map((existing) =>
          existing.id === questionId ? savedQuestion : existing,
        ),
      }));

      setDraftAnswers((prev) => ({
        ...prev,
        [stageId]: (prev[stageId] ?? []).map((existing) =>
          existing.id === questionId ? savedQuestion : existing,
        ),
      }));

      setEditingQuestionIds((prev) => {
        const next = new Set(prev);
        next.delete(questionId);
        return next;
      });
      showNotification(
        response.data.message || 'Changes saved successfully',
        'success',
      );
      // An Email stage completes when its last answer is saved, which can allocate the next stage.
      void fetchApplicantStages(true);
    } catch (error) {
      showNotification('Error saving changes', 'error');
      console.error('Error saving answer:', error);
    } finally {
      setSavingQuestionId(null);
    }
  };

  const enterEditQuestion = (id: number) => {
    setEditingQuestionIds((prev) => new Set(prev).add(id));
  };

  const discardQuestion = (id: number, stageId: number) => {
    setDraftAnswers((prev) => ({
      ...prev,
      [stageId]: (prev[stageId] ?? []).map((q) =>
        q.id === id ? { ...JSON.parse(JSON.stringify((applicantQuestions[stageId] ?? []).find((s) => s.id === id) || q)) } : q
      ),
    }));
    setEditingQuestionIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
  };

  const handleResendQuestion = async (questionAnswerId: number) => {
    try {
      setResendingQuestionId(questionAnswerId);

      const response = await api.post<ResendQuestionResponse>(
        APPLICANT_ENDPOINTS.resendQuestion(questionAnswerId),
        { resentBy: user?.email || '', },
        { headers: { ...API_HEADERS.RESEND_QUESTION } }
      );

      showNotification(
        response.data.message ||
          'Question re-sent successfully. The candidate will receive it on WhatsApp.',
        'success'
      );

      const resendPatch = (question: ApplicantQuestion) =>
        applyResendToQuestion(question, response.data);

      setApplicantQuestions((prev) =>
        patchQuestionInGroupedLists(prev, questionAnswerId, resendPatch)
      );
      setDraftAnswers((prev) =>
        patchQuestionInGroupedLists(prev, questionAnswerId, resendPatch)
      );
    } catch (error) {
      console.error('Error resending question:', error);

      showNotification(
        'Failed to re-send the question. Please try again.',
        'error'
      );
    } finally {
      setResendingQuestionId(null);
    }
  };

  const handleConfirmResendQuestion = async () => {
    if (resendConfirmQuestionId == null) {
      return;
    }
    const questionAnswerId = resendConfirmQuestionId;
    await handleResendQuestion(questionAnswerId);
    setResendConfirmQuestionId(null);
  };

  const renderAnswer = (question: ApplicantQuestion, isEditing: boolean) => {
    switch (question.answerType) {
      case 'text':
        if (!isEditing) {
          return (
            <Box
              sx={{
                width: '100%',
                backgroundColor: '#FFFFFF',
                border: '1px solid #D0D5DD',
                borderRadius: '8px',
                padding: '12px 16px',
                minHeight: '38px',
                boxSizing: 'border-box',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '15px',
                  fontWeight: 400,
                  lineHeight: 1.5,
                  color: '#101828',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  textAlign: 'justify',
                }}
              >
                {question.answerText ?? ''}
              </Typography>
            </Box>
          );
        }
        return (
          <TextField
            multiline
            value={question.answerText ?? ''}
            onChange={(e) => handleAnswerChange(question.id, 'answerText', e.target.value)}
            disabled={!isEditing}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '8px 12px',
                minHeight: '38px',
                height: 'auto',
                alignItems: 'flex-start',
                backgroundColor: '#fff',
              },
              '& .MuiOutlinedInput-input': {
                padding: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'Inter',
                fontSize: '15px',
                fontWeight: 500,
                color: '#000000',
                textAlign: 'justify',
              },
            }}
          />
        );
      case 'number':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <IconButton
              onClick={() => handleAnswerChange(question.id, 'answerNumber', Math.max(0, (question.answerNumber ?? 0) - 1))}
              disabled={!isEditing || (question.answerNumber ?? 0) <= 0}
              sx={{ width: 32, height: 32, borderRadius: '8px', border: '1px solid #702DFF', p: 0, '&:hover': { backgroundColor: '#F1F3FF' }, '&.Mui-disabled': { borderColor: '#D0D5DD' } }}
            >
              <RemoveIcon sx={{ fontSize: 16, color: isEditing ? '#702DFF' : '#D0D5DD' }} />
            </IconButton>
            <TextField
              type="number"
              inputProps={{ min: 0, step: 1 }}
              value={question.answerNumber ?? ''}
              onChange={(e) => handleAnswerChange(question.id, 'answerNumber', e.target.value === '' ? null : Math.max(0, parseInt(e.target.value, 10)))}
              onBlur={(e) => {
                if (e.target.value === '') {
                  handleAnswerChange(question.id, 'answerNumber', 0);
                }
              }}
              onKeyDown={(e) => {
                if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              disabled={!isEditing}
              sx={{
                width: 60,
                '& .MuiOutlinedInput-root': {
                  height: 32,
                  borderRadius: '8px',
                  '& fieldset': { borderColor: '#702DFF' },
                  '&:hover fieldset': { borderColor: '#702DFF' },
                  '&.Mui-focused fieldset': { borderColor: '#702DFF' },
                  '&.Mui-disabled fieldset': { borderColor: '#D0D5DD' },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '4px 8px',
                  textAlign: 'center',
                  fontFamily: 'Inter',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#000000',
                  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { display: 'none' },
                  MozAppearance: 'textfield',
                },
              }}
            />
            <IconButton
              onClick={() => handleAnswerChange(question.id, 'answerNumber', (question.answerNumber ?? 0) + 1)}
              disabled={!isEditing}
              sx={{ width: 32, height: 32, borderRadius: '8px', border: '1px solid #702DFF', p: 0, '&:hover': { backgroundColor: '#F1F3FF' }, '&.Mui-disabled': { borderColor: '#D0D5DD' } }}
            >
              <AddIcon sx={{ fontSize: 16, color: isEditing ? '#702DFF' : '#D0D5DD' }} />
            </IconButton>
          </Box>
        );
      case 'decimal':
        return (
          <Box sx={{ width: '200px' }}>
            <TextField
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              value={question.answerDecimal ?? ''}
              onChange={(e) => handleAnswerChange(question.id, 'answerDecimal', e.target.value === '' ? null : toNonNegativeNumber(e.target.value))}
              onKeyDown={(e) => {
                if (['e', 'E', '+', '-'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              disabled={!isEditing}
              sx={{
                '& .MuiOutlinedInput-input': {
                  fontFamily: 'Inter',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#000000',
                },
              }}
            />
          </Box>
        );
      case 'currency':
        return (
          <Box sx={{ width: '200px' }}>
            <TextField
              type="text"
              value={formatNumberWithCommas(question.answerCurrency)}
              onChange={(e) => {
                const rawValue = removeCommas(e.target.value);
                if (rawValue === '' || /^[0-9]*\.?[0-9]*$/.test(rawValue)) {
                  handleAnswerChange(question.id, 'answerCurrency', rawValue === '' ? null : parseFloat(rawValue));
                }
              }}
              onKeyDown={(e) => {
                if (['e', 'E', '+', '-'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              disabled={!isEditing}
              slotProps={{ input: { startAdornment: <InputAdornment position="start">{salaryCurrency || 'USD'}</InputAdornment> } }}
              sx={{
                '& .MuiOutlinedInput-input': {
                  fontFamily: 'Inter',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#000000',
                },
              }}
            />
          </Box>
        );
      case 'boolean':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: -1, height: '38px' }}>
            <Radio
              checked={question.answerBoolean === true}
              onChange={() => isEditing && handleAnswerChange(question.id, 'answerBoolean', true)}
              disabled={!isEditing}
              sx={{ color: '#D0D5DD', '&.Mui-checked': { color: '#702DFF' }, p: '6px' }}
            />
            <Typography sx={{ fontFamily: 'Inter', fontSize: '15px', fontWeight: 500, color: '#000000', ml: -1 }}>Yes</Typography>
            <Radio
              checked={question.answerBoolean === false}
              onChange={() => isEditing && handleAnswerChange(question.id, 'answerBoolean', false)}
              disabled={!isEditing}
              sx={{ color: '#D0D5DD', '&.Mui-checked': { color: '#702DFF' }, p: '6px' }}
            />
            <Typography sx={{ fontFamily: 'Inter', fontSize: '15px', fontWeight: 500, color: '#000000', ml: -1 }}>No</Typography>
          </Box>
        );
      case 'date':
        return (
          <Box sx={{ width: '200px' }}>
            <DatePickerField
              value={question.answerDate ? dayjs(question.answerDate) : null}
              onChange={(date: string | null) => handleAnswerChange(question.id, 'answerDate', date || null)}
              disabled={!isEditing}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  // A stage that is not complete can be triggered once its Jira status is reached (the backend
  // `action.reason` explains why it is disabled: another stage in progress, prerequisite missing, ...).
  const shouldShowTriggerButton = (stage: ApplicantStageView): boolean =>
    hasPermission(PERMISSION_IDS.APPLICANT_ADDITIONAL_INFO_MANUAL_STAGE_TRIGGER) &&
    stage.state !== 'complete';

  const triggerButtonLabel = (stage: ApplicantStageView, isLoading: boolean): string => {
    const allocateOnly = stage.action.type === 'allocate';
    if (isLoading) {
      return allocateOnly ? 'Allocating...' : 'Triggering...';
    }
    return stage.action.text || (allocateOnly ? 'Allocate' : 'Allocate & Trigger');
  };

  const handleTriggerQuestions = async (stage: ApplicantStageView) => {
    if (!id) return;

    try {
      setTriggeringStageId(stage.vacancyStageId);

      // 200 = started, 400 = not started; both carry the same StageRunResult body.
      const response = await applicantService.triggerStage(
        Number(id),
        stage.vacancyStageId,
        user?.email || '',
      );
      const result = response.data;

      if (result?.started) {
        showNotification(
          result.message || `${stage.name} questions triggered successfully`,
          'success',
        );
      } else {
        showNotification(result?.message || `${stage.name} could not be started`, 'error');
      }
      await fetchApplicantStages(true);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        'Failed to trigger interview questions';

      showNotification(errorMessage, 'error');

      console.error('Error triggering interview questions:', error);
      await fetchApplicantStages(true);
    } finally {
      setTriggeringStageId(null);
    }

  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Box className="view-section">
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: '-12px', right: '-12px' }}>
            {!isEditFirst && hasPermission(PERMISSION_IDS.APPLICANT_ADDITIONAL_INFO_EDIT) && (
              <IconButton className="view-edit-icon" onClick={() => setIsEditFirst(true)}>
                <EditIcon />
              </IconButton>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {salaryFields.map((field) => (
              <Box key={field.key} sx={{ width: field.width }}>
                <Typography className="view-label-title">{field.label}</Typography>
                {
                  isEditFirst ? (
                    <>
                      {field.key === 'currentSalary' && (
                        <TextField
                          type="number"
                          inputProps={{ min: 0 }}
                          value={currentSalaryInput}
                          onChange={(e) => {
                            setCurrentSalaryInput(e.target.value);
                            setValues({ ...values, currentSalary: toNullableNonNegativeNumber(e.target.value) });
                          }}
                          onKeyDown={(e) => {
                            if (['-', 'e', 'E', '+'].includes(e.key)) e.preventDefault();
                          }}
                        />
                      )}
                      {field.key === 'expectedSalary' && (
                        <TextField
                          type="number"
                          inputProps={{ min: 0 }}
                          value={expectedSalaryInput}
                          onChange={(e) => {
                            setExpectedSalaryInput(e.target.value);
                            setValues({ ...values, expectedSalary: toNullableNonNegativeNumber(e.target.value) });
                          }}
                          onKeyDown={(e) => {
                            if (['-', 'e', 'E', '+'].includes(e.key)) e.preventDefault();
                          }}
                        />
                      )}
                    </>
                  ) : <Typography className="view-value-title">{field.value}</Typography>
                }
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box className="view-section">
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: '-12px', right: '-12px' }}>
            {!isEditSecond && hasPermission(PERMISSION_IDS.APPLICANT_ADDITIONAL_INFO_EDIT) && (
              <IconButton className="view-edit-icon" onClick={() => setIsEditSecond(true)}>
                <EditIcon />
              </IconButton>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {joiningFields.map((field) => (
              <Box key={field.label} sx={{ width: field.width }}>
                <Typography className="view-label-title">{field.label} {(isEditSecond && field.label === 'Notice Period') ? ' (in days)' : ''}</Typography>
                {
                  isEditSecond ? (
                    <>
                      {field.label === 'Notice Period (in days)' && (
                        <TextField
                          type="number"
                          inputProps={{ min: 0 }}
                          value={values.noticePeriod}
                          onChange={(e) => setValues({ ...values, noticePeriod: toNonNegativeText(e.target.value) })}
                          onKeyDown={(e) => {
                            if (['-', 'e', 'E', '+', '.'].includes(e.key)) e.preventDefault();
                          }}
                        />
                      )}
                      {field.label === 'Joining Date' && (
                        <DatePickerField
                          value={values.joiningDate ? dayjs(values.joiningDate) : null}
                          onChange={(date: string | null) => setValues({ ...values, joiningDate: date ? date : '' })}
                        />
                      )}
                      {field.label === 'Joining Note' && (
                        <TextField
                          value={values.joiningNote}
                          onChange={(e) => setValues({ ...values, joiningNote: e.target.value as string })}
                        />
                      )}
                    </>
                  ) : <Typography className="view-value-title">{field.value}</Typography>
                }
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Action Buttons */}
      {(isEditFirst || isEditSecond) && hasPermission(PERMISSION_IDS.APPLICANT_ADDITIONAL_INFO_EDIT) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, width: '1246px', mt: 1 }}>
          <Button
            variant="outlined"
            disabled={isSaving}
            className="edit-section-cancel-button"
            onClick={() => {
              setIsEditFirst(false);
              setIsEditSecond(false);
              setValues(initialValues);
              setCurrentSalaryInput(salaryInputText(initialValues?.currentSalary));
              setExpectedSalaryInput(salaryInputText(initialValues?.expectedSalary));
            }}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            isLoading={isSaving}
            disabled={isSaving}
            onClick={handleSaveChanges}
            className="edit-section-save-button"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}

      {/* Interview Questions Section */}
      {isLoadingQuestions ? (
        <Box className="view-section">
          <Loading message="Loading interview questions..." />
        </Box>
      ) :
        (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <div className="view-section-title">Interview Questions</div>
            </Box>

            {stages.map((stage) => {
              const stageId = stage.vacancyStageId;
              const stageQuestions = draftAnswers[stageId] ?? [];
              return (
              <Box key={stageId} className="view-section">
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#101828',
                      }}
                    >
                      {stage.name}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#667085', mt: 0.25 }}>
                      {stage.stageType === 'GENERAL' ? 'General questions' : 'Interview stage'}
                      {stage.jiraStatusName ? ` · ${stage.jiraStatusName}` : ''}
                      {stage.total > 0 ? ` · ${stage.answered}/${stage.total} answered` : ''}
                    </Typography>
                  </Box>

                  {shouldShowTriggerButton(stage) && (
                    <Tooltip title={stage.action.reason ?? ''} arrow>
                    <span style={{ display: 'inline-flex' }}>
                    <Button
                      variant="contained"
                      disabled={triggeringStageId === stageId || !stage.action.enabled}
                      isLoading={triggeringStageId === stageId}
                      onClick={() => handleTriggerQuestions(stage)}
                      sx={{
                        backgroundColor: '#702DFF',
                        color: '#FFFFFF',
                        minWidth: '154px',
                        height: '40px',
                        borderRadius: '10px',
                        textTransform: 'none',
                        px: 2,
                        '&:hover': {
                          backgroundColor: '#6920E5',
                        },
                      }}
                    >
                      {triggerButtonLabel(stage, triggeringStageId === stageId)}
                    </Button>
                    </span>
                    </Tooltip>
                  )}
                </Box>

                {stageQuestions.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      backgroundColor: '#F1F3FF',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      minHeight: '50px',
                      overflow: 'visible',
                    }}
                  >
                    <img
                      src={InfoIcon}
                      alt="Info"
                      width={29}
                      height={29}
                    />

                    <Typography
                      sx={{
                        color: '#0000008C',
                        fontSize: '16px',
                        fontWeight: 500,
                      }}
                    >
                      No interview questions allocated to this stage.
                    </Typography>
                  </Box>
                ) : (
                  stageQuestions.map((question, index) => {
                    const isEditing = editingQuestionIds.has(question.id);

                    const originalQuestion = (applicantQuestions[stageId] ?? []).find(
                      (q) => q.id === question.id
                    );

                    const hasChanges = (() => {
                      if (!originalQuestion) return false;

                      switch (question.answerType) {
                        case 'text':
                          return (question.answerText ?? '') !== (originalQuestion.answerText ?? '');

                        case 'number':
                          return question.answerNumber !== originalQuestion.answerNumber;

                        case 'decimal':
                          return question.answerDecimal !== originalQuestion.answerDecimal;

                        case 'currency':
                          return question.answerCurrency !== originalQuestion.answerCurrency;

                        case 'boolean':
                          return question.answerBoolean !== originalQuestion.answerBoolean;

                        case 'date':
                          if (!question.answerDate && !originalQuestion.answerDate) return false;
                          if (!question.answerDate || !originalQuestion.answerDate) return true;

                          return !dayjs(question.answerDate).isSame(
                            dayjs(originalQuestion.answerDate),
                            'day'
                          );

                        default:
                          return false;
                      }
                    })();

                    const questionMeta = resolveQuestionAnswerMeta(question);

                    return (
                      <Box
                        key={question.id}
                        data-testid={`question-row-${question.id}`}
                        sx={{
                          backgroundColor: '#F4F3FF',
                          borderRadius: '8px',
                          padding: '14px 16px',
                          mb: 2,
                          position: 'relative',
                        }}
                      >
                        {/* Header */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1.5,
                              flex: 1,
                            }}
                          >
                            {/* Question Number */}
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                minWidth: 20,
                                borderRadius: '50%',
                                backgroundColor: '#7927FF',
                                color: '#FFF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '11px',
                                fontWeight: 600,
                                mt: '2px',
                              }}
                            >
                              {index + 1}
                            </Box>

                            <Box sx={{ flex: 1 }}>
                              {/* Question */}
                              <Typography
                                sx={{
                                  fontSize: '14px',
                                  fontWeight: 400,
                                  color: '#344054',
                                  lineHeight: '20px',
                                }}
                              >
                                {question.question}
                              </Typography>

                              {questionMeta?.displayText && (
                                <Box
                                  data-testid={`question-answer-meta-${question.id}`}
                                  sx={{ mt: '4px' }}
                                >
                                  <Tooltip
                                    title={questionMeta.tooltip}
                                    arrow
                                    slotProps={{
                                      tooltip: {
                                        sx: {
                                          fontSize: '12px',
                                          borderRadius: '6px',
                                        },
                                      },
                                    }}
                                  >
                                    <Typography
                                      component="span"
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        color: questionMeta.color,
                                        backgroundColor: questionMeta.backgroundColor,
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        whiteSpace: 'nowrap',
                                        cursor: 'default',
                                      }}
                                    >
                                      {questionMeta.displayText}
                                    </Typography>
                                  </Tooltip>
                                </Box>
                              )}
                            </Box>
                          </Box>

                          {/* Refresh Icon */}
                          {hasPermission(PERMISSION_IDS.APPLICANT_ADDITIONAL_INFO_QUESTION_RESEND) && (
                            <Tooltip
                              title={getResendButtonTooltip(question, Boolean(canResendForStage[stageId]))}
                              arrow
                              slotProps={{
                                tooltip: {
                                  sx: { fontSize: '12px', borderRadius: '6px', maxWidth: 280 },
                                },
                              }}
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={resendButtonAriaLabel}
                                  disabled={
                                    resendingQuestionId === question.id ||
                                    !canResendForStage[stageId] ||
                                    !isQuestionAnswered(question) ||
                                    question.isResent === true
                                  }
                                  onClick={() => setResendConfirmQuestionId(question.id)}
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    backgroundColor: '#7927FF',
                                    color: '#FFF',
                                    borderRadius: '8px',
                                    ml: 1,

                                    '&:hover': {
                                      backgroundColor: '#6920e5',
                                    },

                                    '&.Mui-disabled': {
                                      backgroundColor: '#D0D5DD',
                                      opacity: 0.6,
                                      cursor: 'not-allowed',
                                    },
                                  }}
                                >
                                  {resendingQuestionId === question.id ? (
                                    <CircularProgress size={18} sx={{ color: '#FFFFFF' }} />
                                  ) : (
                                    <img
                                      src={ResendIcon}
                                      alt=""
                                      width={34}
                                      height={34}
                                    />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>

                        {/* Answer */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 1,
                            mt: 1.5,
                            ml: '28px',
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            {renderAnswer(question, isEditing)}
                          </Box>

                          {!isEditing &&
                            hasPermission(
                              PERMISSION_IDS.APPLICANT_ADDITIONAL_INFO_EDIT
                            ) && (
                              <Tooltip
                                title={
                                  question.isResent === true
                                    ? 'Waiting for a new answer'
                                    : ''
                                }
                                arrow
                                slotProps={{
                                  tooltip: {
                                    sx: { fontSize: '12px', borderRadius: '6px' },
                                  },
                                }}
                              >
                                <span>
                                  <IconButton
                                    onClick={() => enterEditQuestion(question.id)}
                                    disabled={question.isResent === true}
                                    sx={{
                                      p: 0.5,
                                      alignSelf: 'center',
                                      '&.Mui-disabled': {
                                        opacity: 0.4,
                                        cursor: 'not-allowed',
                                      },
                                    }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                        </Box>
                        {/* Save/Discard buttons below answer */}
                        {isEditing &&
                          hasPermission(
                            PERMISSION_IDS.APPLICANT_ADDITIONAL_INFO_EDIT
                          ) && (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 1,
                                mt: 1,
                                ml: '28px',
                              }}
                            >
                              <Button
                                variant="outlined"
                                disabled={savingQuestionId === question.id}
                                onClick={() =>
                                  discardQuestion(question.id, stageId)
                                }
                              >
                                Discard
                              </Button>

                              <Button
                                variant="contained"
                                disabled={
                                  savingQuestionId === question.id ||
                                  !hasChanges
                                }
                                isLoading={savingQuestionId === question.id}
                                onClick={() =>
                                  handleSaveAnswer(question.id, stageId)
                                }
                              >
                                Save
                              </Button>
                            </Box>
                          )}
                      </Box>
                    );
                  })
                )}
              </Box>
              );
            })}
            {stagesInfo && stagesInfo.totalStages > stages.length && (
              <Typography sx={{ fontSize: '13px', color: '#667085', mt: 1 }}>
                {`${stagesInfo.totalStages - stages.length} more stage(s) will appear when the candidate reaches their Jira status.`}
              </Typography>
            )}
          </>
        )}

      {/* Notification Component */}
      <ConfirmationModal
        open={resendConfirmQuestionId !== null}
        onClose={() => {
          if (resendingQuestionId === null) {
            setResendConfirmQuestionId(null);
          }
        }}
        onConfirm={() => {
          void handleConfirmResendQuestion();
        }}
        title={
          (findQuestionById(applicantQuestions, resendConfirmQuestionId ?? -1)?.resentCount ?? 0) > 0
            ? 'Re-send this question again?'
            : 'Re-send this question?'
        }
        message={buildResendConfirmMessage(
          resendConfirmQuestionId != null
            ? findQuestionById(applicantQuestions, resendConfirmQuestionId)
            : null,
        )}
        confirmText="Re-send"
        cancelText="Cancel"
        severity="warning"
        isActioning={resendingQuestionId !== null}
      />
      <Notification
        open={notification.open}
        onClose={hideNotification}
        message={notification.message}
        severity={notification.severity}
        title={notification.title}
      />
    </Box>
  );
};

export default AdditionalInfo; 